// "Pakete gösterge ekle" › Katalog sekmesi: görünür etiketli arama + "Pakettekileri gizle" +
// kategori ağacı (kategori kutusu alt ağacın eklenebilir yapraklarını seçer). Seçim, iki sekmenin
// ortak `picks` haritasına yazılır; ağaçtan gelen yeni seçim "Katalog" kaynaklıdır, AI seçimleri
// korunur.

import { useCallback, useId, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import type { KpiMetric } from "@mahir/shared";
import { KPI_PACKAGES_COPY } from "../../lib/kpi-packages-copy";
import { hideInPackage } from "../../lib/kpi-package-add";
import {
  catalogTreeMetrics,
  isEligibleItem,
  itemDisabledReason,
  type DraftContext,
  type PackagePick,
} from "../../lib/kpi-package-draft";
import { CATALOG_TREE_KEYS, CatalogCheckboxTree } from "./CatalogCheckboxTree";
import { btnGhost, inputStyle, labelStyle } from "./kpi-form-styles";

const A = KPI_PACKAGES_COPY.add;
const C = KPI_PACKAGES_COPY.page;

export interface PackageAddCatalogTabProps {
  metrics: readonly KpiMetric[];
  ctx: DraftContext;
  members: ReadonlySet<string>;
  picks: ReadonlyMap<string, PackagePick>;
  setPicks: Dispatch<SetStateAction<ReadonlyMap<string, PackagePick>>>;
  narrow?: boolean;
}

export function PackageAddCatalogTab(p: PackageAddCatalogTabProps): JSX.Element {
  const { metrics, ctx, members, picks, setPicks } = p;
  const searchId = useId();
  const hideId = useId();
  const [search, setSearch] = useState("");
  const [hide, setHide] = useState(false);

  const allTree = useMemo(() => catalogTreeMetrics(metrics), [metrics]);
  const treeMetrics = useMemo(
    () => (hide ? hideInPackage(allTree, members) : allTree),
    [hide, allTree, members],
  );
  const treeIds = useMemo(() => new Set(allTree.map((m) => m.id)), [allTree]);
  const selected = useMemo(() => new Set([...members, ...picks.keys()]), [members, picks]);

  const isDisabled = useCallback(
    (m: KpiMetric, sel: boolean): string | null =>
      members.has(m.id) ? C.inPackage : itemDisabledReason(m, ctx, sel && picks.has(m.id)),
    [members, ctx, picks],
  );
  const isEligible = useCallback(
    (m: KpiMetric): boolean => !members.has(m.id) && isEligibleItem(m.id, ctx),
    [members, ctx],
  );

  function onChange(next: Set<string>): void {
    setPicks((prev) => {
      const out = new Map<string, PackagePick>();
      // ağaçta olmayan (ör. AI'nın önerdiği) seçimler korunur
      for (const [id, pick] of prev) if (!treeIds.has(id)) out.set(id, pick);
      for (const id of next) {
        if (members.has(id) || !treeIds.has(id)) continue;
        out.set(id, prev.get(id) ?? { metricId: id, source: "manual", reason: null });
      }
      return out;
    });
  }

  return (
    <div className="grid gap-2" style={{ gridTemplateColumns: "minmax(0, 1fr)" }}>
      <div className="flex flex-wrap items-end gap-3">
        <div style={{ flex: "1 1 220px", minWidth: 0 }}>
          <label htmlFor={searchId} style={labelStyle}>
            {A.search}
          </label>
          <div className="flex items-center gap-1">
            <input
              id={searchId}
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ ...inputStyle, flex: 1, minWidth: 0 }}
            />
            {search ? (
              <button
                type="button"
                style={{ ...btnGhost, minHeight: 44, minWidth: 44 }}
                aria-label={A.clearSearch}
                title={A.clearSearch}
                onClick={() => setSearch("")}
              >
                ×
              </button>
            ) : null}
          </div>
        </div>
        <label
          htmlFor={hideId}
          className="flex items-center gap-2 text-sm"
          style={{ minHeight: 44, color: "var(--color-text-2)" }}
        >
          <input
            id={hideId}
            type="checkbox"
            checked={hide}
            onChange={(e) => setHide(e.target.checked)}
          />
          {A.hideInPackage}
        </label>
      </div>
      <div
        className="rounded-lg"
        style={{ border: "1px solid var(--color-border)", overflow: "hidden", minWidth: 0 }}
      >
        <CatalogCheckboxTree
          collapseKey={CATALOG_TREE_KEYS.packageAdd}
          metrics={treeMetrics}
          selected={selected}
          search={search}
          ariaLabel={A.title}
          maxHeight={420}
          alwaysScroll
          {...(p.narrow === undefined ? {} : { narrow: p.narrow })}
          eligible={isEligible}
          inPackage={members}
          isDisabled={isDisabled}
          onChange={onChange}
        />
      </div>
    </div>
  );
}
